import { instance } from "api/apiBase";
import { ROLE } from "providers/Auth";
import { Partner } from "types/landlord/partner";
import { PropertyManagementSystem } from "shared/types/partner";
import { objectToSnakeCase } from "utils/snakeCase";
import { SyncOption } from "views/settings-integration/integrations/pms/constants";

/**
 * Need to define properties for renter and tenancy summary
 */
export type DashboardPayload = {
  billing: {
    ach_link: string;
    bank_account_required: boolean;
    plaid_link: string;
    opaque_id: string;
  };
  algolia: {
    client_search_key?: string | null;
    index_name?: string | null;
  };
  property_management_system: {
    name: PropertyManagementSystem | "";
    last_synced_at: string | null;
  };
  status: "started" | "approved" | "rejected";
  property_summary: {
    occupied: number;
    total: number;
  };
  notifications: {
    payment_verifications?: number;
    identity_verifications?: number;
  };
};

export const getDashboard = async (): Promise<DashboardPayload> => {
  return (await instance.get(`/portal/v1/dashboard`)).data;
};

export const addTeamMember = async ({
  name,
  email,
  role
}: {
  name: string;
  email: string;
  role: ROLE;
}) => {
  return (await instance.post(`/portal/v1/users`, { name, email, role })).data;
};

export const resendInviteForTeamMember = async ({
  userId
}: {
  userId: string;
}) => {
  return (await instance.post(`portal/v1/users/${userId}/reset_password`)).data;
};

type SettingsPayload = {
  settings?: {
    name: string;
    readonly: boolean;
    value: string | null;
    title: string;
  }[];
};
export const QUERY_SETTINGS = "settings";
export const getSettings = async (): Promise<SettingsPayload> =>
  (await instance.get(`/portal/v1/settings`)).data;

export type SaveSettingsPayload = {
  resmanApiKey?: string;
  resmanPartnerId?: string;
  resmanAccountId?: string;
  buildiumClientId?: string;
  buildiumClientSecret?: string;
  buildiumEmail?: string;
  buildiumPassword?: string;
  buildiumSubdomain?: string;
  rentManagerCompanyCode?: string;
  rentersWarehouseApiKey?: string;
  rentersWarehouseUrl?: string;
  rentersWarehouseSalesforceDomain?: string;
  rentersWarehouseSalesforceClientId?: string;
  rentersWarehouseSalesforceClientSecret?: string;
  rentersWarehouseSalesforceUsername?: string;
  rentersWarehouseSalesforcePassword?: string;
  manageAmericaCompanyId?: string;
  yardiWebServiceUrl?: string;
  yardiUsername?: string;
  yardiPassword?: string;
  yardiServerName?: string;
  yardiDatabase?: string;
  yardiEnableApplicationCharge?: boolean;
  yardiApplicationChargeCode?: string;
  yardiEnableScreeningServicesCharge?: boolean;
  yardiScreeningServicesChargeCode?: string;
  yardiGeneralLedgerAccount?: string;
  rentvineAccount?: string;
  rentvineApiKey?: string;
  rentvineApiSecret?: string;
  rentvineSync?: SyncOption;
  quextCustomerId?: string;
  appfolioUsername?: string;
  appfolioPassword?: string;
  appfolioUrl?: string;
  appfolioSync?: string;
  appfolioUseReports?: boolean;
  appfolioUsePropertyGroups?: boolean;
  appfolioSelectedPropertyGroups?: string[];
  appfolioEnableRently?: boolean;
  entrataDomain?: string;
  entrataUsername?: string;
  entrataPassword?: string;
  entrataMfaEmail?: string;
  accessKey?: string;
  pms_prospect_creation_trigger?: "decision" | "submission" | "start";
  pms_prospect_creation_on_approve?: boolean;
  pms_prospect_creation_on_conditional_approve?: boolean;
  pms_prospect_creation_on_decline?: boolean;
  pms_import_inventory?: boolean;
  rent_manager_create_twa?: boolean;
  googleAnalyticsId?: string | string[] | null;
  propertywareClientId?: string;
  propertywareClientSecret?: string;
  propertywareSystemId?: string;
  propertywareSync?: SyncOption;
};

export const saveSettings = async (
  saveSettingsPayload: SaveSettingsPayload
) => {
  return (
    await instance.post(`/portal/v1/settings`, {
      settings: objectToSnakeCase(saveSettingsPayload)
    })
  ).data;
};

type CurrentPartnerPayload = {
  partner: Partner;
};
export const QUERY_CURRENT_PARTNER = "current_partner";
export const getCurrentPartner = async (): Promise<CurrentPartnerPayload> =>
  (await instance.get(`portal/v1/partners/current`)).data;

export type PartnersCredentialsPayload = {
  partner: {
    jwt_overriden_key: string | null;
  };
};

export const getPartnersCredentials =
  async (): Promise<PartnersCredentialsPayload> => {
    return (await instance.get(`/portal/v1/partners/credentials`)).data;
  };

export type PartnerDefaultEnrollmentsPayload = {
  default_enrollment_type: string;
  state_province_defaults: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>;
};

export const getOperatingStatesAndProvinces = async (): Promise<{
  operating_states_and_provinces: string[];
}> => {
  return (
    await instance.get(`/portal/v1/partners/operating_states_and_provinces`)
  ).data;
};

export const getPartnerDefaultEnrollments =
  async (): Promise<PartnerDefaultEnrollmentsPayload> => {
    return (await instance.get(`/portal/v1/partners/default_enrollments`)).data;
  };

export type EnrollmentSettings = {
  default_enrollment_type: string;
  states_and_provinces: Array<{
    state_province: string;
    default_enrollment_type: string;
  }>;
};

export const updatePartnerDefaultEnrollments = async (
  data: EnrollmentSettings
) => {
  return (await instance.patch(`/portal/v1/partners/default_enrollments`, data))
    .data;
};
